import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import NewAppShell from '../components/NewAppShell';
import Plans from './Plans';
import { saldoDeCreditos } from '../components/logada/creditos';
import { apiFetch } from '../config/api';
import PlanoMonitorIa from '../components/logada/PlanoMonitorIa';
import { planConfigs } from '../config/planConfigs';
import { usePlanos, nivelDoPlano } from '../config/planos';
import { priceIdFor, MESES_GRATIS_NO_ANUAL, mensalEquivalente } from '../config/stripePrices';
import { useIdioma } from '../i18n';
import { useFormato } from '../i18n/formato';
import '../styles/NewPlans.css';

/**
 * Plano e uso no desenho da área logada nova, ao lado de pages/Plans, que
 * segue no repositório para voltar.
 *
 * `/plans` é pública: sem sessão, esta tela devolve a Plans de sempre, com o
 * cabeçalho do site — a página de preços de quem chega de fora não muda.
 *
 * Com sessão, a tela abre pelo uso (que antes ficava na Conta), compra de
 * créditos junto do saldo, e os planos numa tabela de comparação montada de
 * `planConfigs` — o mesmo objeto que o backend aplica, então nenhuma célula é
 * digitada à mão.
 */

const semLimite = (valor) => valor === 'Unlimited' || valor === -1 || valor == null;

// shortcut: a mesma tabela de config/planos (lá não é exportada), até as telas antigas saírem.
const SUPORTE = {
  email: 'plano.suporteEmail',
  chat: 'plano.suporteChat',
  priority_chat: 'plano.suportePrioritario',
  dedicated: 'plano.suporteDedicado'
};

const STATUS_DA_COBRANCA = {
  succeeded: { chave: 'pagamentos.pago', tom: 'ok' },
  pending: { chave: 'pagamentos.pendente', tom: 'warn' },
  failed: { chave: 'pagamentos.falhou', tom: 'crit' }
};

/* Voltar para Basic é cancelar a assinatura: Basic não cobra, e o que o
   backend sabe fazer é `cancel_at_period_end`. Um checkout de $0 criaria uma
   segunda assinatura ao lado da que continua cobrando.
   shortcut: copiado de pages/Plans, até a antiga ser apagada. */
const acaoPara = (nome, atual) => {
  /* O plano atual antes do Business: quem já é Business via "Fale com a
     gente" na própria coluna, em vez de "Plano atual". */
  if (nome === atual) return { chave: 'planosTela.planoAtual', tipo: 'atual' };
  if (nome === 'Business') return { chave: 'planosTela.faleConosco', tipo: 'contato' };
  if (nome === 'Basic') return { chave: 'planosTela.cancelarAssinatura', tipo: 'cancelar' };
  return {
    chave: nivelDoPlano(nome) > nivelDoPlano(atual) ? 'planosTela.melhorar' : 'planosTela.rebaixar',
    tipo: 'checkout'
  };
};

const PlanoEUso = ({ user }) => {
  const { t } = useIdioma();
  const { numero, data, moeda } = useFormato();
  const planos = usePlanos();
  const plano = user.plan || 'Basic';
  // Plano, créditos e cobranças são do gestor (Fase 4a); o analista vê o uso.
  const analista = user.papel === 'analista';
  const [uso, setUso] = useState(null);
  const [pacotes, setPacotes] = useState([]);
  /* undefined enquanto carrega; null quando não há cobrança nenhuma. */
  const [ultima, setUltima] = useState(undefined);
  const [anual, setAnual] = useState(false);
  const [aviso, setAviso] = useState({ tipo: '', texto: '' });
  const [ocupado, setOcupado] = useState(false);
  const [comprando, setComprando] = useState(null);
  const [modalBusiness, setModalBusiness] = useState(false);
  const dialogoRef = useRef(null);
  const [enviandoContato, setEnviandoContato] = useState(false);
  const [contato, setContato] = useState({
    name: user.name || '',
    email: user.email || '',
    company: '',
    message: t('area.plano.mensagemBusiness')
  });

  /* `<dialog>` nativo, como o do Admin: o navegador prende o foco, fecha com
     Esc e devolve o foco ao botão que o abriu. */
  useEffect(() => {
    if (modalBusiness) dialogoRef.current?.showModal();
  }, [modalBusiness]);

  useEffect(() => {
    apiFetch('/user/usage')
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => json?.usage && setUso(json.usage))
      .catch(() => setAviso({ tipo: 'crit', texto: 'erros.uso' }));

    /* Lista vazia é estado normal: sem os price IDs no servidor a compra não
       existe, e o bloco simplesmente não oferece pacote. */
    apiFetch('/credits/packs')
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => setPacotes(json?.packs || []))
      .catch(() => {});

    apiFetch('/payments/history')
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => setUltima(json?.payments?.[0] || null))
      .catch(() => setUltima(null));
  }, []);

  /* A volta do Stripe depois da troca de plano cai aqui (o backend devolve
     /plans?upgrade=ok). O webhook é quem muda o plano, e pode chegar depois do
     redirecionamento — daí "em instantes". */
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('upgrade') !== 'ok') return;
    setAviso({ tipo: 'ok', texto: 'planosTela.trocaRecebida' });
    window.history.replaceState({}, '', window.location.pathname);
  }, []);

  // shortcut: checkout e cancelamento de pages/Plans; créditos e contato de pages/UserSettings.
  const irParaCheckout = async (nome) => {
    const priceId = priceIdFor(nome, anual ? 'annual' : 'monthly');
    if (!priceId) return setAviso({ tipo: 'crit', texto: 'erros.planoIndisponivel' });
    setOcupado(true);
    setAviso({ tipo: '', texto: '' });
    try {
      const r = await apiFetch('/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ priceId, fromPlans: true })
      });
      const dados = await r.json();
      if (r.ok && dados.url) {
        window.location.href = dados.url;
        return;
      }
      setAviso({ tipo: 'crit', texto: dados.error || 'erros.requisicao' });
    } catch {
      setAviso({ tipo: 'crit', texto: 'erros.conexao' });
    }
    setOcupado(false);
  };

  const cancelar = async () => {
    if (!window.confirm(t('conta.confirmarCancelamento'))) return;
    setOcupado(true);
    try {
      const r = await apiFetch('/cancel-subscription', { method: 'POST' });
      const dados = await r.json();
      if (!r.ok) throw new Error(dados.error || 'erros.cancelamentoAssinatura');
      setAviso({ tipo: 'ok', texto: dados.message || 'planosTela.assinaturaCancelada' });
    } catch (erro) {
      setAviso({ tipo: 'crit', texto: erro.message });
    } finally {
      setOcupado(false);
    }
  };

  const comprarCreditos = async (priceId) => {
    if (comprando) return;
    setComprando(priceId);
    setAviso({ tipo: '', texto: '' });
    try {
      const r = await apiFetch('/credits/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ priceId })
      });
      const dados = await r.json();
      if (!r.ok || !dados.url) throw new Error(dados.error || 'erros.compra');
      window.location.href = dados.url;
    } catch (erro) {
      setAviso({ tipo: 'crit', texto: erro.message });
      setComprando(null);
    }
  };

  const enviarContato = async (e) => {
    e.preventDefault();
    setEnviandoContato(true);
    try {
      const r = await apiFetch('/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contato)
      });
      const dados = await r.json();
      if (!r.ok) throw new Error(dados.error || 'erros.mensagem');
      setAviso({ tipo: 'ok', texto: 'conta.mensagemEnviada' });
      dialogoRef.current?.close();
    } catch (erro) {
      setAviso({ tipo: 'crit', texto: erro.message });
    } finally {
      setEnviandoContato(false);
    }
  };

  const executar = (nome, tipo) => {
    if (ocupado) return;
    if (tipo === 'checkout') return irParaCheckout(nome);
    if (tipo === 'cancelar') return cancelar();
    if (tipo === 'contato') setModalBusiness(true);
  };

  const limite = (total) =>
    semLimite(total) ? t('area.semLimite') : t('area.limiteDoPlano', { total: numero(total) });

  const creditos =
    uso &&
    saldoDeCreditos({
      total: uso.ai_credits.total,
      usados: uso.ai_credits.used,
      extra: uso.ai_credits.extra,
      restantes: uso.ai_credits.remaining
    });

  const sim = (valor) =>
    valor ? t('area.sim') : <span className="lgl-nao" aria-label={t('area.nao')}>—</span>;

  /* Mono só no número: "sem limite" é texto e fica na fonte do texto. */
  const quantia = (valor) =>
    semLimite(valor) ? t('area.semLimite') : <span className="lgl-num">{numero(valor)}</span>;

  const LINHAS = [
    { chave: 'area.plano.linhas.projetos', valor: (c) => quantia(c.limits.projects) },
    { chave: 'area.plano.linhas.paginas', valor: (c) => quantia(c.limits.pages) },
    { chave: 'area.plano.linhas.creditos', valor: (c) => quantia(c.features.monthly_credits) },
    { chave: 'area.plano.linhas.painel', valor: (c) => sim(c.features.dashboard) },
    /* O conector vale do Guru para cima: é a lista de `routes/mcp.js`. */
    { chave: 'area.plano.linhas.conector', valor: (c, nome) => sim(nivelDoPlano(nome) >= nivelDoPlano('Guru')) },
    { chave: 'area.plano.linhas.integracoes', valor: (c) => sim(c.features.custom_integrations) },
    { chave: 'area.plano.linhas.suporte', valor: (c) => t(SUPORTE[c.features.support_type] || SUPORTE.email) },
    {
      chave: 'area.plano.linhas.retencao',
      valor: (c) =>
        semLimite(c.features.data_retention_days)
          ? t('area.semLimite')
          : t('area.plano.dias', { n: c.features.data_retention_days })
    }
  ];

  const preco = (p) => {
    if (p.contato) return t('precos.personalizado');
    const gratuito = p.preco.mensal === 0;
    const valor = anual && !gratuito ? mensalEquivalente(p.nome) : p.preco.mensal;
    return (
      <>
        <span className="lgl-preco">{moeda(Math.round(valor * 100), 'usd')}</span>{' '}
        <span className="lgl-unidade">
          {anual && !gratuito ? t('precos.anualDetalhe', { valor: p.preco.anual }) : t('precos.porMes')}
        </span>
      </>
    );
  };

  const statusDaUltima = ultima && (STATUS_DA_COBRANCA[ultima.status] || { chave: null, tom: 'neutral' });

  return (
    <NewAppShell
      user={user}
      titulo={t('menu.planoEUso')}
      subtitulo={`${plano}${t(plano === 'Basic' ? 'planosTela.gratuitoParaSempre' : 'planosTela.cobradoStripe')}.`}
    >
      {aviso.texto && (
        <p className={`sp-alert sp-alert-${aviso.tipo}`} role={aviso.tipo === 'crit' ? 'alert' : 'status'}>
          {t(aviso.texto)}
        </p>
      )}

      {uso && (
        <section className="lg-numeros lgl-uso" aria-label={t('area.plano.uso')}>
          <div>
            <div className="lg-rotulo">{t('conta.projetos')}</div>
            <div className="lg-valor">{numero(uso.projects.used)}</div>
            <div className="lg-nota">{limite(uso.projects.total)}</div>
          </div>
          <div>
            <div className="lg-rotulo">{t('conta.paginasEVideos')}</div>
            <div className="lg-valor">{numero(uso.pages.used)}</div>
            <div className="lg-nota">
              {uso.pages.details &&
                `${t('conta.detalhePaginas', {
                  sites: numero(uso.pages.details.sites),
                  youtube: numero(uso.pages.details.youtube)
                })} · `}
              {limite(uso.pages.total)}
            </div>
          </div>
          <div>
            <div className="lg-rotulo">{t('conta.creditos')}</div>
            <div className="lg-valor">
              {numero(creditos.restantes)}{' '}
              <small>{t('area.de', { total: numero(creditos.capacidade) })}</small>
            </div>
            <span
              className={`lg-barra${creditos.pct <= 20 ? ' is-baixo' : ''}`}
              role="img"
              aria-label={t('area.creditosRestantes', {
                restantes: numero(creditos.restantes),
                total: numero(creditos.capacidade)
              })}
            >
              <span style={{ width: `${creditos.pct}%` }} />
            </span>
            <div className="lg-nota">
              {creditos.extra > 0
                ? t('area.creditosOrigem', { mes: numero(creditos.doMes), extra: numero(creditos.extra) })
                : t('area.creditosDoMes', { mes: numero(creditos.doMes) })}
            </div>
            {/* `reset_date` é a data da última renovação, não da próxima: a
                tela antiga a chamava de "Renova em" e mostrava uma data passada. */}
            <div className="lg-nota">
              {uso.ai_credits.reset_date &&
                `${t('area.plano.ultimaRenovacao', { data: data(uso.ai_credits.reset_date) })} · `}
              {t('conta.tokensPorAnalise', { n: numero(uso.ai_credits.max_tokens) })}
            </div>
            {pacotes.length > 0 && !analista && (
              <div className="lgl-pacotes">
                <p>{t('conta.comprarChamada')}</p>
                <div className="lgl-pacotes-botoes">
                  {pacotes.map((pacote) => (
                    <button
                      key={pacote.priceId}
                      type="button"
                      className="sp-btn sp-btn-secondary"
                      onClick={() => comprarCreditos(pacote.priceId)}
                      disabled={comprando !== null}
                    >
                      {comprando === pacote.priceId
                        ? t('conta.abrindo')
                        : t('conta.comprarPacote', { n: numero(pacote.credits) })}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      <section className="lg-cartao lgl-planos" aria-labelledby="lgl-planos-titulo">
        <div className="lg-cartao-cabeca lgl-planos-cabeca">
          <h2 id="lgl-planos-titulo">{t('menu.planos')}</h2>
          <div className="lgl-ciclo">
            <div className="sp-seg" role="group" aria-label={t('precos.ciclo')}>
              <button type="button" className="sp-seg-opt" aria-pressed={!anual} onClick={() => setAnual(false)}>
                {t('precos.mensal')}
              </button>
              <button type="button" className="sp-seg-opt" aria-pressed={anual} onClick={() => setAnual(true)}>
                {t('precos.anual')}
              </button>
            </div>
            <span className="lgl-desconto">{t('precos.mesesGratis', { n: MESES_GRATIS_NO_ANUAL })}</span>
          </div>
        </div>

        <div className="lgl-rolagem">
          <table className="lgl-tabela">
            <thead>
              <tr>
                <th scope="col">
                  <span className="lg-sr">{t('area.plano.recurso')}</span>
                </th>
                {planos.map((p) => (
                  <th scope="col" key={p.nome} className={p.nome === plano ? 'is-atual' : undefined}>
                    <span className="lgl-nome">
                      {p.nome}
                      {p.nome === plano && <span className="lgl-seu"> · {t('area.plano.seuPlano')}</span>}
                    </span>
                    <span className="lgl-descricao">{p.descricao}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row">{t('area.plano.linhas.preco')}</th>
                {planos.map((p) => (
                  <td key={p.nome} className={p.nome === plano ? 'is-atual' : undefined}>
                    {preco(p)}
                  </td>
                ))}
              </tr>
              {LINHAS.map((linha) => (
                <tr key={linha.chave}>
                  <th scope="row">{t(linha.chave)}</th>
                  {planos.map((p) => (
                    <td key={p.nome} className={p.nome === plano ? 'is-atual' : undefined}>
                      {linha.valor(planConfigs[p.nome], p.nome)}
                    </td>
                  ))}
                </tr>
              ))}
              <tr className="lgl-acoes">
                <th scope="row">
                  <span className="lg-sr">{t('execucao.acoes')}</span>
                </th>
                {planos.map((p) => {
                  const acao = acaoPara(p.nome, plano);
                  return (
                    <td key={p.nome} className={p.nome === plano ? 'is-atual' : undefined}>
                      {acao.tipo === 'atual' ? (
                        <span className="lgl-atual">{t(acao.chave)}</span>
                      ) : analista ? null : (
                        <button
                          type="button"
                          className={`sp-btn ${acao.tipo === 'contato' ? 'sp-btn-primary' : 'sp-btn-secondary'}`}
                          disabled={ocupado}
                          onClick={() => executar(p.nome, acao.tipo)}
                        >
                          {t(acao.chave)}
                        </button>
                      )}
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <PlanoMonitorIa anual={anual} analista={analista} aoAvisar={setAviso} />

      {analista ? (
        <p className="lgl-aviso">{t('comum.soGestor')}</p>
      ) : (
        <section className="lg-cartao lgl-cobranca" aria-labelledby="lgl-cobranca-titulo">
          <div className="lg-cartao-cabeca">
            <h2 id="lgl-cobranca-titulo">{t('area.plano.ultimaCobranca')}</h2>
            <Link to="/payments">{t('planosTela.historicoCompleto')}</Link>
          </div>
          {ultima === undefined && <p className="lgl-aviso">{t('comum.carregando')}</p>}
          {ultima === null && (
            <p className="lgl-aviso">
              {t(plano === 'Basic' ? 'planosTela.basicSemCobranca' : 'planosTela.semCobrancas')}
            </p>
          )}
          {ultima && (
            <div className="lgl-cobranca-linha">
              <span className="sp-mono lgl-data">{data(ultima.created * 1000)}</span>
              <span>{ultima.plan_name || ultima.description || t('pagamentos.assinatura')}</span>
              <span className="sp-mono">{moeda(ultima.amount, ultima.currency)}</span>
              <span className={`sp-tag sp-tag-${statusDaUltima.tom}`}>
                {statusDaUltima.chave ? t(statusDaUltima.chave) : ultima.status}
              </span>
              {ultima.status === 'failed' && <span className="lgl-falhou">{t('area.plano.cobrancaFalhou')}</span>}
            </div>
          )}
        </section>
      )}

      {modalBusiness && (
        <dialog
          ref={dialogoRef}
          className="sp-modal-box lgl-dialogo"
          aria-labelledby="lgl-business-titulo"
          onClose={() => setModalBusiness(false)}
        >
          <h3 id="lgl-business-titulo">{t('conta.business')}</h3>
          <p>{t('conta.businessChamada')}</p>
          <form onSubmit={enviarContato} className="lgl-business">
            {[
              { id: 'name', chave: 'conta.nome', tipo: 'text', auto: 'name' },
              { id: 'email', chave: 'conta.email', tipo: 'email', auto: 'email' },
              { id: 'company', chave: 'conta.empresa', tipo: 'text', auto: 'organization' }
            ].map((c) => (
              <div className="lgl-business-campo" key={c.id}>
                <label htmlFor={`lgl-${c.id}`}>{t(c.chave)}</label>
                <input
                  id={`lgl-${c.id}`}
                  type={c.tipo}
                  autoComplete={c.auto}
                  className="sp-input"
                  value={contato[c.id]}
                  onChange={(e) => setContato({ ...contato, [c.id]: e.target.value })}
                  required
                  disabled={enviandoContato}
                />
              </div>
            ))}
            <div className="lgl-business-campo">
              <label htmlFor="lgl-message">{t('conta.mensagem')}</label>
              <textarea
                id="lgl-message"
                className="sp-input"
                rows="4"
                value={contato.message}
                onChange={(e) => setContato({ ...contato, message: e.target.value })}
                required
                disabled={enviandoContato}
              />
            </div>
            <div className="sp-modal-foot">
              <button
                type="button"
                className="sp-btn sp-btn-secondary"
                onClick={() => dialogoRef.current.close()}
                disabled={enviandoContato}
              >
                {t('comum.cancelar')}
              </button>
              <button type="submit" className="sp-btn sp-btn-primary" disabled={enviandoContato}>
                {enviandoContato ? t('conta.enviando') : t('conta.enviarMensagem')}
              </button>
            </div>
          </form>
        </dialog>
      )}
    </NewAppShell>
  );
};

/* Sem sessão, a página pública de preços de sempre. */
const NewPlans = ({ user }) => (user ? <PlanoEUso user={user} /> : <Plans user={user} />);

export default NewPlans;
